import { Typography, useTheme } from '@hmsexp/component-library';
import React, { useCallback, useMemo, useState } from 'react';
import { View } from 'react-native';
import GridLayout from '../../../../components/GridLayout';
import { overviewCompStyling } from './styles';
import { useSelector } from 'react-redux';
import OverviewLoader from './OverviewLoader';
import MultiFeatureCard from '../../../../components/Common/Card/MultiOptionsCard/MultiFeatureCard';
import PieChartContainer from './components/PieChartContainer';
import AdditionalMetrixComparison from './components/AdditionalMetrixComparison';
import NetPromotorScore from './components/NetPromotorScore';
import LowerSampleSizeFooter from '../../../../components/Common/LowerSampleSizeFooter';
import TopBottomProperties from './components/TopBottomProperties';
import SidebarComponent from './components/SidebarComponent';
import { makeObjectOutOfArrays } from '../../utils';
import ReviewTopicPlot from './components/ReviewTopicPlot';
import { get } from 'lodash';
import { useHistory } from 'react-router-dom';
import { OVERVIEW_WIDGET_DATA } from '../../constants';

const OverviewComp = () => {
  const [openSideBar, setOpenSidebar] = useState(false);
  const [selectedCardData, setSelectedCardData] = useState({});
  const [selectedCardId, setSelectedCardId] = useState(null);

  const theme = useTheme();
  const styles = overviewCompStyling(theme);
  function onChartClick(d, itemData) {
    if(!itemData?.disableChartClick){
      setOpenSidebar(true);
      if (itemData?.dialClickPageType) {
        setSelectedCardId(itemData?.dialClickPageType);
      }
      setSelectedCardData(itemData);
    }
    
  }
  const {
    isFetchingOverviewData,
    overViewData = [],
    OverviewDataFetchingError
  } = useSelector(e => e.fetchOverviewDataReducer);

  const { applied_filters, applied_filter_values, filtersLoading } = useSelector(e => e.filterReducer);

  const timeperiodLabel = get(applied_filters, 'timePeriod[0].fullLabel', 'Last 90 days');

  const benchMarkLabel = useMemo(() => {
    return get(applied_filters, 'benchmark[0].label', 'TEst');
  }, [applied_filters]);

  function pieChartTooptip(scoreSize, label) {
    return (
      <View style={styles.pieChartContainer}>
        <Typography textProps={{ style: styles.tooltipValue }} variant='button' color='white'>
          {scoreSize}
        </Typography>
        <Typography textProps={{ style: styles.tooltipLabel }} variant='button' color='white'>
          {timeperiodLabel}
        </Typography>
      </View>
    );
  }

  const history = useHistory();
  function onTopicClick(e) {
    const appliedFilterValues = { ...applied_filter_values, topic_filters: [[e]] };
    const stringified = encodeURIComponent(JSON.stringify(appliedFilterValues));
    history.push(`/dashboard/feedback?filters=${stringified}`);
  }

  const renderItem = useCallback(({ item }) => {
    switch (item.type) {
      case 'TEXT_HEADER':
        return <Typography {...item?.data?.typographyProps}>{item?.data?.title}</Typography>;
      case 'PIE_CHART':
        let { columns: pieColumns, metric_data: pieMetricData, metric_count } = item?.cstData || {};
        let pieDataObj = makeObjectOutOfArrays(pieColumns, pieMetricData?.[0]);
        const value = pieDataObj[item?.valueKeyToPlot];

        const benchmarkSampleSize = (value - pieDataObj?.benchmark_value || 0)?.toFixed(2);

        const subLabel = `${benchmarkSampleSize > 0 ? '+' : ''}  ${benchmarkSampleSize}`;

        const options = {
          ...item?.data,
          centerLabel: {
            label: value,
            subLabel: subLabel
          }
        };
        let pieChartSubtitle = `${timeperiodLabel}: ${item?.title}`;
        const lowSampleSize = item?.lowSampleSize
        const isLowSampleSize = metric_count?.[0]?.[0] < lowSampleSize;
        return (
          <MultiFeatureCard
            title={item?.title}
            subTitle={pieChartSubtitle}
            renderContent={(activeSwitch, contentRef) => (
              <PieChartContainer
                isLowSampleSize={isLowSampleSize}
                onChartClick={e => onChartClick(e, { ...item, subTitle: pieChartSubtitle })}
                value={value}
                data={item}
                options={options}
                ref={contentRef}
                activeSwitch={activeSwitch}
                tooltipComponent={() => pieChartTooptip(subLabel, benchMarkLabel)}
              />
            )}
            enableActionBtn={true}
            style={{ childContainer: { paddingVertical: 20, paddingTop: 40 } }}
            enableSwitchTab={item?.enableSwitchTab}
            switchOptions={item?.switchOptions}
            renderFooter={() => (
              <View>
                {isLowSampleSize && <LowerSampleSizeFooter size={lowSampleSize} />}
                <Typography fontWeightType='regular' variant='button'>
                  {item?.footerText}
                </Typography>
              </View>
            )}
          />
        );
      case 'NET_PROMOTER_SCORE':
        const npsSubtitle = `${timeperiodLabel}: Likelihood to Recommend (0-10)`;
        let { metric_count: nps_metric_count } = item?.cstData || {};
        const npsLowSampleSize = item?.lowSampleSize
        const isNpsLowSampleSize = nps_metric_count?.[0]?.[0] < npsLowSampleSize;
        return (
          <MultiFeatureCard
            title={item?.title}
            subTitle={npsSubtitle}
            renderContent={(activeSwitch, contentRef) => (
              <NetPromotorScore
                isLowSampleSize={isNpsLowSampleSize}
                onChartClick={e => onChartClick(e, { ...item, subTitle: npsSubtitle })}
                pieChartTooptip={pieChartTooptip}
                data={{ ...item, subTitle: npsSubtitle }}
                options={item?.data}
                ref={contentRef}
                activeSwitch={activeSwitch}
              />
            )}
            enableActionBtn={true}
            style={{ childContainer: { paddingVertical: 20, paddingTop: 40 } }}
            enableSwitchTab={item?.enableSwitchTab}
            switchOptions={item?.switchOptions}
            renderFooter={() => (
              <View>
                {isNpsLowSampleSize && <LowerSampleSizeFooter size={npsLowSampleSize} />}
                <Typography fontWeightType='regular' variant='button'>
                  {item?.footerText}
                </Typography>
              </View>
            )}
          />
        );
      case 'ADDITIONAL_METRICS_COMPARISON_CHART':
        let { columns = [], metric_data = [] } = item?.cstData || {};
        let dataObj = makeObjectOutOfArrays(columns, metric_data?.[0]);
        const isData = !!item?.cstData && item.cstData?.metric_data?.length;
        const subTitle = `Time Period: ${timeperiodLabel}`;
        return (
          <MultiFeatureCard
            title={item?.title}
            subTitle={subTitle}
            renderContent={(_, contentRef) => (
              <AdditionalMetrixComparison
                widgetData={{ ...item, subTitle }}
                ref={contentRef}
                isnoData={!isData}
                data={dataObj}
                options={item?.data}
              />
            )}
            enableActionBtn={true}
            style={{ childContainer: { paddingVertical: 20 } }}
            enableSwitchTab={item?.enableSwitchTab}
            switchOptions={item?.switchOptions}
            renderChartHeader={() => { }}
            renderFooter={() => (
              <Typography fontWeightType='regular' variant='button'>
                {item?.footerText}
              </Typography>
            )}
          />
        );
      case 'TOP_BOTTOM_TOPICS':
        return (
          <MultiFeatureCard
            title={item?.title}
            subTitle={timeperiodLabel}
            enableActionBtn={true}
            enableSwitchTab={item?.enableSwitchTab}
            switchOptions={item?.switchOptions}
            renderContent={(activeSwitch, contentRef) => {
              return (
                <ReviewTopicPlot
                  type={activeSwitch}
                  onTopicClick={onTopicClick}
                  widgetData={item}
                  itemData={item?.cstDataV2}
                  contentRef={contentRef}
                />
              );
            }}
            renderFooter={() => (
              <Typography fontWeightType='regular' variant='button'>
                {item?.footerText}
              </Typography>
            )}
          />
        );
      case 'BOTTOM_5_PROPERTIES':
      case 'TOP_5_PROPERTIES':
        return (
          <MultiFeatureCard
            title={item?.title}
            subTitle={timeperiodLabel}
            style={{ childContainer: { paddingVertical: 20, flex: 1 } }}
            enableActionBtn={true}
            enableSwitchTab={item?.enableSwitchTab || true}
            switchOptions={item?.switchOptions}
            renderContent={(activeSwitch, contentRef) => {
              return <TopBottomProperties lowSampleSize={item?.lowSampleSize} type={activeSwitch} itemData={item} ref={contentRef} />;
            }}
            renderFooter={() => item?.cstData && <LowerSampleSizeFooter size={item?.lowSampleSize} />}
          />
        );
      default:
        return null;
    }
  }, [timeperiodLabel]);

  const renderContent = useCallback(() => {
    if (isFetchingOverviewData || filtersLoading) {
      return <OverviewLoader />;
    }
    return <GridLayout gap={30} data={overViewData} renderItem={renderItem} />;
    // return <GridLayout gap={30} data={OVERVIEW_WIDGET_DATA} renderItem={renderItem} />;
  }, [isFetchingOverviewData, filtersLoading, overViewData]);

  return (
    <View style={{ padding: 20 }}>
      <SidebarComponent
        selectedCardId={selectedCardId}
        setSelectedCardId={setSelectedCardId}
        selectedCardData={selectedCardData}
        visible={openSideBar}
        onClose={() => setOpenSidebar(false)}
      />
      {renderContent()}
    </View>
  );
};
export default OverviewComp;
